<template>
    <b-modal
        id="portfolio-tags-management-modal"
        modal-class="apprentice-default-modal"
        :title="$t('portfolio.edit_tags')"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        :ok-title="$t('common.save')"
        @ok="StoreTags"
        @show="onShow"
        size="md"
    >
        <div class="tags-management">
            <b-alert v-if="hasError" variant="danger" show>
                {{ $t("portfolio.not_all_tags_filled_in_correctly_name_and_colour_code_are_mandatory") }}
            </b-alert>
            <b-alert variant="warning" show>
                {{ $t("portfolio.assignment_tags.warning_school_wide") }}
            </b-alert>
            <div class="tags-overview">
                <design2021-apprentice-data-table
                    ref="colorTags"
                    class="tags-overview-table"
                    :fields="fields"
                    :items="tags"
                >
                    <template #cell(name)="row">
                        <div class="name" :class="row.item.name === '' && 'empty'">
                            <b-form-input
                                v-model="row.item.name"
                                :id="`new-portfolio-tag-input-${row.index}`"
                                :key="`new-portfolio-tag-input-${row.index}`"
                                :placeholder="$t('portfolio.tag_name')"
                            >
                            </b-form-input>
                        </div>
                    </template>

                    <template #cell(color)="row">
                        <div class="tag-color">
                            <design-2021-colorpicker
                                :id="`portfolio-tag-color-input-${row.index}`"
                                :key="`portfolio-tag-color-input-${row.index}`"
                                :color="row.item.color"
                                @on-close="addColorToTag($event, row.index)"
                            >
                            </design-2021-colorpicker>
                        </div>
                    </template>

                    <template #cell(action)="row">
                        <div class="actions" v-if="items.length !== 1">
                            <b-button
                                class="btn-remove btn-remove-tag"
                                variant="danger-icon-only"
                                @click="removeTag(row.index)"
                            >
                            </b-button>
                        </div>
                    </template>
                </design2021-apprentice-data-table>

                <div class="add-tag">
                    <b-button class="btn-add btn-add-portfolio-tag" variant="primary" @click="addTag">
                        {{ $t("portfolio.add_tag") }}
                    </b-button>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import Design2021ApprenticeDataTable from "../../../common/design-2021-apprentice-data-table.vue";

export default {
    name: "design-2021-portfolio-tags-management-modal-component",
    props: {
        groupId: {
            type: Number,
            required: true,
        },
    },
    components: { Design2021ApprenticeDataTable },
    data() {
        return {
            fields: [
                {
                    key: "name",
                    label: this.$t("common.name"),
                    sortable: true,
                },
                {
                    key: "color",
                    label: this.$t("common.colour"),
                },
                {
                    key: "action",
                    label: this.$t("common.actions"),
                },
            ],
            items: [
                {
                    name: "",
                    color: "#000",
                },
            ],
            hasError: false,
            errorIds: [],
        };
    },

    mounted() {
        this.getAvailableTags();
    },

    methods: {
        onShow() {
            this.getAvailableTags();
        },

        getAvailableTags() {
            this.axios.get(this.routes.assignmentsTagsUpdate(this.groupId)).then(
                (response) => {
                    if (response.data.length > 0) {
                        this.items = response.data.map((tag) => tag);
                    }
                },
                (error) => {
                    this.alert.ajaxError(error);
                }
            );
        },

        addColorToTag(color, index) {
            const calculatedIndex =
                (this.$refs.colorTags.current_page - 1) * this.$refs.colorTags.perPageSelected + index;

            this.items = this.items.map((item, key) => {
                if (key === calculatedIndex) {
                    item.color = color;
                }
                return item;
            });
        },

        addTag() {
            this.items.push({
                name: "",
                color: "#000",
            });
        },

        removeTag(index) {
            this.items.splice(index, 1);
        },

        StoreTags(bvModalEvent) {
            if (this.hasErrors()) {
                bvModalEvent.preventDefault();
                return;
            }

            let data = new FormData();
            let count = 0;
            for (const tag of this.items) {
                if (tag.id) {
                    data.append(`tag[${count}][id]`, tag.id);
                }
                data.append(`tag[${count}][name]`, tag.name);
                data.append(`tag[${count}][color]`, tag.color);
                count++;
            }

            const headers = { "Content-Type": "multipart/form-data" };
            this.alert.saving();
            this.axios.post(this.routes.assignmentsTagsUpdate(this.groupId), data, headers).then(
                (response) => {
                    this.items = response.data;
                    this.$emit("update-tags", response.data);
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                }
            );
        },

        hasErrors() {
            let errorsFound = false;
            this.hasError = false;
            this.errorIds = [];
            for (let i = 0; i < this.items.length; i++) {
                if (this.items[i].name === "") {
                    errorsFound = true;
                    this.hasError = true;
                    this.errorIds.push(i);
                }
            }

            return errorsFound;
        },
    },

    computed: {
        tags() {
            let tags = this.items;
            if (this.errorIds.length > 0) {
                tags = this.items.map((item, index) => {
                    if (this.errorIds.indexOf(index) !== -1) {
                        item._rowVariant = "danger";
                    } else {
                        delete item._rowVariant;
                    }
                    return item;
                });
            }

            return tags;
        },
    },
};
</script>

<style scoped lang="scss">
.tags-overview-table ::v-deep {
    .table-responsive {
        overflow-x: inherit;
    }
}

.name {
    input.form-control {
        margin-bottom: 0;

        &:focus {
            box-shadow: 0 0 0 0.2rem rgba($primary, 65%);
        }
    }
}

.color-picker ::v-deep {
    border-radius: $border-radius-content;

    .color-picker-container {
        height: inherit !important;

        input.form-control {
            &:focus {
                outline: none;
                box-shadow: none;
            }
        }

        .current-color {
            display: flex;
            border-top-right-radius: $border-radius-content;
            border-bottom-right-radius: $border-radius-content;
        }

        i {
            color: $white !important;
            height: auto !important;
            align-self: center;
            border: none;
        }
    }

    &:focus-within {
        box-shadow: 0 0 0 0.2rem rgba($primary, 65%);
    }
}

.add-tag {
    margin-top: map-get($grid-gutter-spacing, "forty-two");
    margin-bottom: map-get($grid-gutter-spacing, "twenty-one");

    .btn-add-portfolio-tag {
        width: 100%;
    }
}

@media (min-width: map-get($grid-breakpoints, "md")) {
    .add-tag {
        margin-top: map-get($grid-gutter-spacing, "forty-two");

        .btn-add-portfolio-tag {
            width: auto;
            margin-left: auto;
        }
    }
}
</style>
